import { Badge } from '~/components/ui/badge'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'
import type { ChecklistItem, SurveyAnswer } from '~/api/types'
import {
    itemCategoryPath,
    itemRows,
    normalizeChoice,
} from './quick-survey-model'

export interface QuestionCardProps {
    item: ChecklistItem
    index: number
    local: Map<string, SurveyAnswer>
    savingKeys: Set<string>
    savedKeys: Set<string>
    chooseChip(fieldId: string, choice: '예' | '아니오' | '모름'): void
    changeNumber(fieldId: string, raw: string): void
}

/** 빠른 설문지 문항 카드 하나 — 항목 헤더 + 필드별 칩/숫자 입력. */
export function QuestionCard({
    item,
    index,
    local,
    savingKeys,
    savedKeys,
    chooseChip,
    changeNumber,
}: QuestionCardProps) {
    const rows = itemRows(item)
    return (
        <li className='border-border bg-card rounded-lg border p-4'>
            <div className='mb-3 flex flex-wrap items-start justify-between gap-2'>
                <div className='min-w-0'>
                    <div className='mb-1 flex flex-wrap items-center gap-1.5'>
                        <Badge tone='neutral'>
                            {item.part || `#${index + 1}`}
                        </Badge>
                        <Badge tone='info'>{item.maxScore}점</Badge>
                    </div>
                    <p className='text-foreground text-sm font-semibold whitespace-pre-line'>
                        {item.itemNumber ? (
                            <span className='text-muted-foreground'>
                                {item.itemNumber}.{' '}
                            </span>
                        ) : null}
                        {item.title}
                    </p>
                    {itemCategoryPath(item) ? (
                        <p className='text-muted-foreground mt-0.5 text-xs'>
                            {itemCategoryPath(item)}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className='space-y-2'>
                {rows.map((row) => {
                    const answer = local.get(row.key)
                    const choice = normalizeChoice(answer)
                    const saving = savingKeys.has(row.key)
                    const saved = savedKeys.has(row.key)
                    return (
                        <div
                            key={row.key}
                            className='border-border bg-background/40 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border px-3 py-2'>
                            <div className='min-w-[140px] flex-1'>
                                <p className='text-foreground text-sm font-medium'>
                                    {row.label}
                                    {row.unit ? (
                                        <span className='text-muted-foreground'>
                                            {' '}
                                            ({row.unit})
                                        </span>
                                    ) : null}
                                </p>
                                {row.question && row.question !== row.label ? (
                                    <p className='text-muted-foreground mt-0.5 text-xs leading-relaxed'>
                                        {row.question}
                                    </p>
                                ) : null}
                            </div>

                            <div className='flex flex-wrap items-center gap-1.5'>
                                {row.input === 'yesno' ? (
                                    <>
                                        <ChipButton
                                            active={choice === '예'}
                                            onClick={() =>
                                                chooseChip(row.key, '예')
                                            }>
                                            예
                                        </ChipButton>
                                        <ChipButton
                                            active={choice === '아니오'}
                                            onClick={() =>
                                                chooseChip(row.key, '아니오')
                                            }>
                                            아니오
                                        </ChipButton>
                                    </>
                                ) : row.input === 'number' ? (
                                    <Input
                                        type='number'
                                        inputMode='decimal'
                                        aria-label={`${row.label} 값${row.unit ? ` (${row.unit})` : ''}`}
                                        value={
                                            answer?.unknown
                                                ? ''
                                                : (answer?.value ?? '')
                                        }
                                        onChange={(e) =>
                                            changeNumber(
                                                row.key,
                                                e.target.value
                                            )
                                        }
                                        placeholder={
                                            row.unit
                                                ? `숫자 입력 (${row.unit})`
                                                : '숫자 입력'
                                        }
                                        className='h-8 w-28'
                                    />
                                ) : (
                                    <Input
                                        type='text'
                                        aria-label={`${row.label} 답변`}
                                        value={
                                            answer?.unknown
                                                ? ''
                                                : (answer?.value ?? '')
                                        }
                                        onChange={(e) =>
                                            changeNumber(
                                                row.key,
                                                e.target.value
                                            )
                                        }
                                        placeholder='답변 입력'
                                        className='h-8 w-48'
                                    />
                                )}
                                <ChipButton
                                    active={choice === '모름'}
                                    onClick={() => chooseChip(row.key, '모름')}>
                                    모름·해당없음
                                </ChipButton>
                                <span
                                    className='w-5 shrink-0 text-center text-xs'
                                    aria-live='polite'>
                                    {saving ? (
                                        <span className='text-muted-foreground'>
                                            …
                                        </span>
                                    ) : saved ? (
                                        <span
                                            className='text-success'
                                            title='저장됨'>
                                            ✓
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        </li>
    )
}

function ChipButton({
    active,
    onClick,
    children,
}: {
    active: boolean
    onClick(): void
    children: React.ReactNode
}) {
    return (
        <button
            type='button'
            aria-pressed={active}
            onClick={onClick}
            className={cn(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-primary/40 text-primary hover:bg-accent bg-card'
            )}>
            {children}
        </button>
    )
}
